import React, { useMemo } from 'react'
import { useQuery as useReactQuery } from 'react-query'
import moment from 'moment'
import { Box, Grid, Pagination, Paper, Skeleton, Stack, TablePagination, Typography } from '@mui/material'
import Chart from 'react-apexcharts'
import MetricCard from '@/components/MetricCard'
import CustomTooltip from '@/components/Tooltip'
import ListOfTalkTable from '@/modules/Analytic/ListOfTalkTable'
import TopTalkerTable from '@/modules/Analytic/TopTalkerTable'
import WordCloud from '@/components/WordCloud'
import {
  getDayToDayConversation,
  getListOfTalk,
  getNegativeTalk,
  getPositiveTalk,
  getTopTalker,
  getTotalTalk,
  getTotalTalker,
  getWordCloud,
} from '@/network/api/keyword'
import { convertChronologicalDataToSeries } from '@/utils/helper'

const KeywordSocialMediaAnalytics = React.memo(() => {
  const [searchParams] = useSearchParams()
  const keyword = searchParams.get('keyword')
  const { endDate, startDate } = useComparisonStore()
  const [page, setPage] = useState<number>(1)
  const [limit, setLimit] = useState<number>(10)

  const params = {
    // escape " character for exact search
    keyword: keyword ? keyword.replace(/"/g, '\\"') : '',
    startDate,
    endDate,
  }

  const { data: totalTalkData, isLoading: totalTalkLoading } = useReactQuery(
    ['keywordTotalTalk', params],
    () => getTotalTalk(params),
    {
      retry: 2,
    },
  )

  const { data: totalTalkerData, isLoading: totalTalkerLoading } = useReactQuery(
    ['keywordTotalTalker', params],
    () => getTotalTalker(params),
    {
      retry: 2,
    },
  )

  const { data: positiveTalkData, isLoading: positiveTalkLoading } = useReactQuery(
    ['keywordPositiveTalk', params],
    () => getPositiveTalk(params),
    {
      retry: 2,
    },
  )

  const { data: negativeTalkData, isLoading: negativeTalkLoading } = useReactQuery(
    ['keywordNegativeTalk', params],
    () => getNegativeTalk(params),
    {
      retry: 2,
    },
  )

  const { data: dayToDayConversationData, isLoading: dayToDayConversationLoading } = useReactQuery(
    ['keywordDayToDayConversation', params],
    () => getDayToDayConversation(params),
    {
      retry: 2,
    },
  )

  const { data: listOfTalkData, isLoading: listOfTalkLoading } = useReactQuery(
    ['keywordListOfTalk', params, limit, page],
    () => getListOfTalk({ ...params, limit: limit, page: page }),
    {
      retry: 2,
    },
  )

  const { data: topTalkerData, isLoading: topTalkerLoading } = useReactQuery(
    ['keywordTopTalker', params],
    () => getTopTalker(params),
    {
      retry: 2,
    },
  )

  const { data: wordCloudData, isLoading: wordCloudLoading } = useReactQuery(
    ['keywordWordCloud', params],
    () => getWordCloud(params),
    {
      retry: 2,
    },
  )

  const dayToDayConversationSeries = useMemo(() => {
    if (!dayToDayConversationData?.payload?.data) return []

    return dayToDayConversationData?.payload?.data?.map((item) => {
      return convertChronologicalDataToSeries(item)
    })
  }, [dayToDayConversationData])

  const handleChangePage = (_event: unknown, newPage: number) => {
    if (newPage < 1) return
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLimit(parseInt(event.target.value))
    setPage(1)
  }

  const TablePageActions = () => {
    return (
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        <Pagination count={listOfTalkData?.payload?.data.totalPages ?? 0} page={page} onChange={handleChangePage} />
      </Box>
    )
  }

  return (
    <Grid container spacing={2} mb={3}>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title="Total Talk"
          recentValue={totalTalkData?.payload?.data?.recentValue ?? 0}
          pastValue={totalTalkData?.payload?.data?.pastValue ?? 0}
          growth={totalTalkData?.payload?.data?.growth ?? 0}
          isLoading={totalTalkLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title="Total Talker"
          recentValue={totalTalkerData?.payload?.data?.recentValue ?? 0}
          pastValue={totalTalkerData?.payload?.data?.pastValue ?? 0}
          growth={totalTalkerData?.payload?.data?.growth ?? 0}
          isLoading={totalTalkerLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title="Positive Talk"
          recentValue={positiveTalkData?.payload?.data?.recentValue ?? 0}
          pastValue={positiveTalkData?.payload?.data?.pastValue ?? 0}
          growth={positiveTalkData?.payload?.data?.growth ?? 0}
          isLoading={positiveTalkLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title="Negative Talk"
          recentValue={negativeTalkData?.payload?.data?.recentValue ?? 0}
          pastValue={negativeTalkData?.payload?.data?.pastValue ?? 0}
          growth={negativeTalkData?.payload?.data?.growth ?? 0}
          isLoading={negativeTalkLoading}
        />
      </Grid>
      <Grid item xs={12}>
        {dayToDayConversationLoading ? (
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <Skeleton variant="rounded" sx={{ width: '100%', height: 400, borderRadius: 4 }} />
          </Box>
        ) : (
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 4,
              position: 'relative',
            }}
          >
            <Stack direction="row">
              <Typography fontWeight={500} fontSize={{ xs: 16, sm: 18 }}>
                Day-to-day Conversation
              </Typography>
              <CustomTooltip title="How is your conversation performance?" />
            </Stack>
            <div style={{ height: '100%', width: '100%', overflow: 'scroll' }}>
              <Box
                sx={{
                  width: { xs: 700, sm: '100%' },
                  height: 400,
                  filter: dayToDayConversationData?.payload?.data?.isLock ? 'blur(8px)' : 'none',
                  opacity: dayToDayConversationData?.payload?.data?.isLock ? 0.5 : 1,
                  zIndex: dayToDayConversationData?.payload?.data?.isLock ? 1 : 0,
                }}
              >
                <Chart
                  type="line"
                  width="100%"
                  height="100%"
                  options={{
                    xaxis: {
                      type: 'category',
                      tickAmount: 'dataPoints',
                      labels: {
                        datetimeUTC: true,
                        formatter: (values) => {
                          const convert =
                            moment(params.endDate)?.diff(params.startDate, 'day') <= 1
                              ? moment(values).format('HH:mm')
                              : moment(values).format('DD MMM')

                          return convert
                        },
                      },
                    },
                    chart: { type: 'line', height: 'auto', animations: { enabled: false } },
                  }}
                  series={dayToDayConversationSeries}
                />
              </Box>
            </div>
          </Paper>
        )}
      </Grid>
      <Grid item xs={12}>
        {listOfTalkLoading ? (
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <Skeleton variant="rounded" sx={{ width: '100%', height: 400, borderRadius: 4 }} />
          </Box>
        ) : (
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              borderRadius: 4,
              position: 'relative',
            }}
          >
            <Stack direction="row">
              <Typography fontWeight={500} fontSize={{ xs: 16, sm: 18 }}>
                List of Talk
              </Typography>
            </Stack>
            <ListOfTalkTable
              data={listOfTalkData?.payload?.data?.result ?? []}
              isLocked={listOfTalkData?.payload?.isLock}
            />
            <TablePagination
              component="div"
              count={listOfTalkData?.payload?.data?.totalPages ?? 0}
              rowsPerPage={limit}
              page={page - 1}
              showFirstButton
              showLastButton
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePageActions}
            />
          </Paper>
        )}
      </Grid>
      <Grid item xs={6}>
        {topTalkerLoading ? (
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <Skeleton variant="rounded" sx={{ width: '100%', height: 300, borderRadius: 4 }} />
          </Box>
        ) : (
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              borderRadius: 4,
              position: 'relative',
            }}
          >
            <Typography fontWeight={500} fontSize={{ xs: 16, sm: 18 }}>
              Top Talker
            </Typography>
            <TopTalkerTable data={topTalkerData?.payload?.data ?? []} isLocked={topTalkerData?.payload?.isLock} />
          </Paper>
        )}
      </Grid>
      <Grid item xs={6}>
        {wordCloudLoading ? (
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <Skeleton variant="rounded" sx={{ width: '100%', height: 300, borderRadius: 4 }} />
          </Box>
        ) : (
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              borderRadius: 4,
              position: 'relative',
            }}
          >
            <Typography fontWeight={500} fontSize={{ xs: 16, sm: 18 }}>
              Word Cloud
            </Typography>
            <Box height="300px">
              <WordCloud data={wordCloudData?.payload?.data ?? []} />
            </Box>
          </Paper>
        )}
      </Grid>
    </Grid>
  )
})

export default KeywordSocialMediaAnalytics
